import React, { useEffect, useState, useCallback } from 'react';
import { Box, IconButton, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, useTheme, Chip, Avatar } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { format, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { apiClient } from '../contexts/AuthContext';
import ScheduleEditDialog from './ScheduleEditDialog';
import ScheduleDialog from './ScheduleDialog';
import { Auxiliaire } from '../types/auxiliaire';
import NoteDialog from './NoteDialog';
import NotesDialog from './NotesDialog';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';

interface WeekViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}

interface DayData {
  date: string;
  day: number;
  month: number;
  year: number;
  weekday: string;
}

interface Schedule {
  id: number;
  auxiliaire_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  is_recurring: boolean;
  day_of_week?: number;
  recurrence_group_id?: string;
  auxiliaire: Auxiliaire;
}

interface NoteItem {
  id: number;
  schedule_id?: number;
  content: string;
  created_at?: string;
}

const WeekView: React.FC<WeekViewProps> = ({ currentDate, onDateChange, dialogOpen, setDialogOpen }) => {
  const [weekData, setWeekData] = useState<DayData[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [auxiliaires, setAuxiliaires] = useState<Auxiliaire[]>([]);
  const theme = useTheme();
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteSchedule, setNoteSchedule] = useState<Schedule | null>(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesByScheduleId, setNotesByScheduleId] = useState<Record<number, NoteItem[]>>({});
  const [loadingNotesFor, setLoadingNotesFor] = useState<Record<number, boolean>>({});
  const [creationDate, setCreationDate] = useState<string | null>(null);

  const getAuxiliaireColor = (auxiliaireId: number) => {
    const colors = [
      '#B5EAD7', // Soft mint
      '#FFDAC1', // Peach
      '#FFB7B2', // Light coral
      '#C7CEEA', // Soft lavender
      '#E2F0CB', // Light sage
      '#FFE5D9', // Pale pink
      '#B5E6FF', // Sky blue
      '#D4A5A5', // Dusty rose
      '#A2D2FF', // Light blue
      '#FFC8DD', // Baby pink
    ];
    return colors[auxiliaireId % colors.length];
  };

  const fetchWeekData = useCallback(async () => {
    try {
      const formattedDate = format(currentDate, 'yyyy-MM-dd');
      const response = await apiClient.get<{ calendar: DayData[] }>(`/api/calendar/week/${formattedDate}`);
      setWeekData(response.data.calendar);
    } catch (error) {
      console.error('Error fetching week data:', error);
    }
  }, [currentDate]);

  const fetchSchedules = useCallback(async () => {
    try {
      const formattedDate = format(currentDate, 'yyyy-MM-dd');
      const response = await apiClient.get<Schedule[]>(`/api/schedules/${formattedDate}`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, [currentDate]);

  const fetchAuxiliaires = useCallback(async () => {
    try {
      const response = await apiClient.get<Auxiliaire[]>('/api/auxiliaires');
      setAuxiliaires(response.data);
    } catch (error) {
      console.error('Error fetching auxiliaires:', error);
    }
  }, []);

  useEffect(() => {
    fetchWeekData();
    fetchSchedules();
    fetchAuxiliaires();
  }, [fetchWeekData, fetchSchedules, fetchAuxiliaires]);

  const handleDeleteSchedule = async (scheduleId: number, deleteAllRecurring: boolean = false) => {
    try {
      const url = deleteAllRecurring 
        ? `/api/schedules/${scheduleId}/recurring`
        : `/api/schedules/${scheduleId}`;
      
      await apiClient.delete(url);
      await fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const handleDeleteClick = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    if (schedule.is_recurring) {
      setDeleteDialogOpen(true);
    } else {
      handleDeleteSchedule(schedule.id);
    }
  };

  const handleSaveSchedule = async (schedule: Omit<Schedule, 'id' | 'auxiliaire'>) => {
    try {
      await apiClient.post('/api/schedules', schedule);
      await fetchSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  const getSchedulesForDate = (date: string) => {
    const filteredSchedules = schedules.filter(schedule => {
      const scheduleStartDate = new Date(schedule.start_date);
      const scheduleEndDate = new Date(schedule.end_date);
      const currentDayDate = new Date(date);
      currentDayDate.setHours(0,0,0,0);
      scheduleStartDate.setHours(0,0,0,0);
      scheduleEndDate.setHours(0,0,0,0);

      if (currentDayDate >= scheduleStartDate && currentDayDate <= scheduleEndDate) {
          return true;
      }
      if (schedule.is_recurring) {
          return schedule.day_of_week === currentDayDate.getDay() && currentDayDate >= scheduleStartDate;
      }
      return false;
    });

    // Sort schedules by start date and start time
    return filteredSchedules.sort((a, b) => {
      // First compare start dates
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      // If dates are equal, compare start times
      return a.start_time.localeCompare(b.start_time);
    });
  };

  const handlePreviousWeek = () => {
    onDateChange(subWeeks(currentDate, 1));
  };

  const handleNextWeek = () => {
    onDateChange(addWeeks(currentDate, 1));
  };

  const formatTimeDisplay = (time: string) => {
    const [hours, minutes] = time.split(':');
    if (hours === '00' && minutes === '00') {
      return 'Minuit';
    }
    if (hours === '12' && minutes === '00') {
      return 'Midi';
    }
    return time.substring(0, 5);
  };

  const getScheduleTimeDisplay = (schedule: Schedule, currentDate: string) => {
    const scheduleStartDate = new Date(schedule.start_date);
    const scheduleEndDate = new Date(schedule.end_date);
    const currentDay = new Date(currentDate);
    currentDay.setHours(0, 0, 0, 0);

    // If the schedule spans multiple days
    if (scheduleStartDate.getDate() !== scheduleEndDate.getDate()) {
      if (isSameDay(currentDay, scheduleStartDate)) {
        // On start day, show from start time to midnight
        return `${formatTimeDisplay(schedule.start_time)} - Minuit`;
      } else if (isSameDay(currentDay, scheduleEndDate)) {
        // On end day, show from midnight to end time
        return `00:00 - ${formatTimeDisplay(schedule.end_time)}`;
      } else {
        // On days in between, show full day
        return '00:00 - Minuit';
      }
    }

    // For single day schedules
    return `${formatTimeDisplay(schedule.start_time)} - ${formatTimeDisplay(schedule.end_time)}`;
  };

  const getScheduleDuration = (schedule: Schedule, currentDate: string) => {
    const scheduleStartDate = new Date(schedule.start_date);
    const scheduleEndDate = new Date(schedule.end_date);
    const currentDay = new Date(currentDate);
    currentDay.setHours(0, 0, 0, 0);

    // Check if it's a full day schedule
    const isFullDay = (startTime: Date, endTime: Date) => {
      return startTime.getHours() === 0 && startTime.getMinutes() === 0 &&
             (endTime.getHours() === 23 || endTime.getHours() === 0) && 
             (endTime.getMinutes() === 59 || endTime.getMinutes() === 0);
    };

    let startTime = new Date();
    let endTime = new Date();

    if (scheduleStartDate.getDate() !== scheduleEndDate.getDate()) {
        if (isSameDay(currentDay, scheduleStartDate)) {
            startTime = new Date(`${schedule.start_date}T${schedule.start_time}`);
            endTime = new Date(currentDay);
            endTime.setHours(23, 59, 59, 999);
        } else if (isSameDay(currentDay, scheduleEndDate)) {
            startTime = new Date(currentDay);
            startTime.setHours(0, 0, 0, 0);
            endTime = new Date(`${schedule.end_date}T${schedule.end_time}`);
        } else {
            // Middle day of a multi-day schedule - return full day
            return -1; // Special value to indicate full day
        }
    } else {
        startTime = new Date(`${schedule.start_date}T${schedule.start_time}`);
        endTime = new Date(`${schedule.end_date}T${schedule.end_time}`);
    }

    // Check if it's a full day schedule
    if (isFullDay(startTime, endTime)) {
        return -1; // Special value to indicate full day
    }

    return (endTime.getTime() - startTime.getTime()) / (1000 * 60); // Duration in minutes
  };

  const handleEditSchedule = async (schedule: Omit<Schedule, 'id' | 'auxiliaire'>) => {
    try {
      await apiClient.put(`/api/schedules/${selectedSchedule?.id}`, schedule);
      await fetchSchedules();
    } catch (error) {
      console.error('Error updating schedule:', error);
    }
  };

  // Removed unused handleSaveSchedule

  const openNoteForSchedule = (schedule: Schedule) => {
    setNoteSchedule(schedule);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async (content: string) => {
    if (!noteSchedule) return;
    try {
      await apiClient.post('/api/notes', {
        schedule_id: noteSchedule.id,
        content,
      });
      setNoteDialogOpen(false);
      setNoteSchedule(null);
      // refresh notes cache for this schedule so the icon appears
      try {
        const res = await apiClient.get<NoteItem[]>(`/api/schedules/${noteSchedule.id}/notes`);
        setNotesByScheduleId(prev => ({ ...prev, [noteSchedule.id]: res.data }));
      } catch {}
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const fetchNotesForSchedule = async (scheduleId: number) => {
    if (loadingNotesFor[scheduleId] || notesByScheduleId[scheduleId]) return;
    setLoadingNotesFor(prev => ({ ...prev, [scheduleId]: true }));
    try {
      const res = await apiClient.get<NoteItem[]>(`/api/schedules/${scheduleId}/notes`);
      setNotesByScheduleId(prev => ({ ...prev, [scheduleId]: res.data }));
    } catch (e) {
      // noop
    } finally {
      setLoadingNotesFor(prev => ({ ...prev, [scheduleId]: false }));
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ 
        display: 'flex', 
        justifyContent: { xs: 'center', sm: 'space-between' },
        alignItems: 'center', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 },
        mb: 3,
        p: 2,
        backgroundColor: 'background.default',
        borderRadius: 2
      }}>
        <IconButton 
          onClick={handlePreviousWeek}
          sx={{ 
            width: 40, 
            height: 40,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Poppins, Inter, sans-serif' }}>
          Semaine du {format(currentDate, 'd MMMM yyyy', { locale: fr })}
        </Typography>
        <IconButton 
          onClick={handleNextWeek}
          sx={{ 
            width: 40, 
            height: 40,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <ChevronRight />
        </IconButton>
      </Paper>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(7, minmax(0, 1fr))' },
        gap: { xs: 2, md: 2 },
        '& > *': {
          flex: 1,
          minWidth: 0
        }
      }}>
        {weekData.map((day) => {
          const daySchedules = getSchedulesForDate(day.date);
          const isToday = format(new Date(), 'yyyy-MM-dd') === day.date;
          const currentDayDateObj = new Date(day.date);

          return (
            <Paper
              key={day.date}
              elevation={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }
              }}
            >
              {/* Open creation dialog when clicking empty area of day card */}
              <Box onClick={() => { setCreationDate(day.date); setDialogOpen(true); }} sx={{ cursor: 'pointer' }}>
              <Box sx={{
                p: 2,
                backgroundColor: isToday ? 'primary.main' : 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    textAlign: 'center',
                    mb: 0.5,
                    color: isToday ? 'common.white' : 'text.secondary',
                    fontWeight: 600
                  }}
                >
                  {day.weekday === 'Monday' ? 'Lundi' :
                   day.weekday === 'Tuesday' ? 'Mardi' :
                   day.weekday === 'Wednesday' ? 'Mercredi' :
                   day.weekday === 'Thursday' ? 'Jeudi' :
                   day.weekday === 'Friday' ? 'Vendredi' :
                   day.weekday === 'Saturday' ? 'Samedi' :
                   day.weekday === 'Sunday' ? 'Dimanche' : day.weekday}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    textAlign: 'center',
                    color: isToday ? 'common.white' : 'text.primary',
                    fontWeight: 500,
                    lineHeight: 1.2
                  }}
                >
                  {day.day}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: isToday ? 'common.white' : 'text.secondary',
                    opacity: 0.8
                  }}
                >
                  {format(new Date(day.date), 'MMMM yyyy', { locale: fr })}
                </Typography>
              </Box>
              
                <Box sx={{ 
                p: 1,
                flexGrow: 1,
                minHeight: { xs: 150, md: 200 },
                maxHeight: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}>
                {daySchedules.map(schedule => {
                  const color = getAuxiliaireColor(schedule.auxiliaire_id);
                  const scheduleStartDate = new Date(schedule.start_date);
                  const scheduleEndDate = new Date(schedule.end_date);
                  const isMultiDay = scheduleStartDate.getDate() !== scheduleEndDate.getDate();
                  const isStartDay = isSameDay(currentDayDateObj, scheduleStartDate);
                  const isEndDay = isSameDay(currentDayDateObj, scheduleEndDate);
                  const isMiddleDay = isMultiDay && !isStartDay && !isEndDay;

                  const startsAtMidnight = (isMultiDay && (isEndDay || isMiddleDay)) || (!isMultiDay && schedule.start_time.startsWith('00:00'));
                  const endsEffectivelyAtMidnight = (isMultiDay && (isStartDay || isMiddleDay)) || (!isMultiDay && (schedule.end_time.startsWith('00:00') || schedule.end_time.startsWith('23:59')));
                  const continuesToNextDay = isMultiDay && !isEndDay;
                  const isFullDayCoverage = startsAtMidnight && endsEffectivelyAtMidnight;

                  const duration = getScheduleDuration(schedule, day.date);
                  const height = isFullDayCoverage ? '100%' : `${Math.max(40, Math.min(100, duration * 0.3))}px`;

                  return (
                    <Tooltip 
                      arrow
                      disableInteractive={false}
                      placement="top"
                      onOpen={() => fetchNotesForSchedule(schedule.id)}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            p: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none'
                          }
                        },
                        arrow: {
                          sx: { color: 'transparent' }
                        }
                      }}
                      title={
                        <Paper elevation={0} sx={{
                          p: 1.25,
                          minWidth: 240,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          background: `linear-gradient(135deg, ${color}33 0%, ${color}88 100%)`,
                          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                          backdropFilter: 'blur(6px)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, color: '#fff', background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)` }}>
                              {schedule.auxiliaire.name?.[0]?.toUpperCase() || 'A'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {schedule.auxiliaire.name}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                                {schedule.auxiliaire.category}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ height: 1, my: 0.75, background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(148,163,184,0.4) 50%, rgba(0,0,0,0) 100%)' }} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <AccessTimeIcon sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{getScheduleTimeDisplay(schedule, day.date)}</Typography>
                            </Box>
                            {schedule.is_recurring && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <AllInclusiveIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption">Récurrent</Typography>
                              </Box>
                            )}
                            {isMultiDay && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CalendarMonthIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption">
                                  {format(new Date(schedule.start_date), 'd MMM', { locale: fr })} → {format(new Date(schedule.end_date), 'd MMM', { locale: fr })}
                                </Typography>
                              </Box>
                            )}
                            {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                              <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.65)', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <StickyNote2OutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Note</Typography>
                                  <Chip size="small" label={`${notesByScheduleId[schedule.id].length}`} sx={{ ml: 1, height: 16 }} />
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                  {notesByScheduleId[schedule.id][0].content}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Ajouter une note">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openNoteForSchedule(schedule); }}>
                                <NoteAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                              <Tooltip title="Gérer les notes">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setNoteSchedule(schedule); setNotesDialogOpen(true); }}>
                                  <EditNoteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Paper>
                      }
                    >
                      <Box
                        key={schedule.id}
                        sx={{
                          position: 'relative',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          px: 1,
                          pt: 1,
                          pb: 1,
                          mb: 0.5,
                          background: `linear-gradient(135deg, ${color}66 0%, ${color}CC 100%)`,
                          color: '#333333',
                          transition: 'all 0.2s ease',
                          height: height,
                          boxSizing: 'border-box',

                          ...(isFullDayCoverage && {
                            pt: 0,
                            pb: 0,
                            mb: 0,
                          }),

                          ...(startsAtMidnight && {
                            pt: 0,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            borderTop: 'none',
                          }),

                          ...(continuesToNextDay && {
                            pb: 0,
                            mb: 0,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            borderBottom: 'none',
                          }),

                          '&:hover': {
                            transform: 'translateY(-1px) scale(1.01)',
                            boxShadow: '0 6px 14px rgba(0,0,0,0.15)'
                          },
                          ...(isMultiDay && {
                            background: isStartDay
                              ? `linear-gradient(to bottom, ${color}66, ${color}CC)`
                              : isEndDay
                              ? `linear-gradient(to top, ${color}66, ${color}CC)`
                              : `linear-gradient(180deg, ${color}80, ${color}80)`
                          }),
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSchedule(schedule);
                          setEditDialogOpen(true);
                        }}
                      >
                        {(isStartDay || !isMultiDay) && (
                          <>
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                right: 2,
                                top: 2,
                                color: '#333333',
                                padding: '2px',
                                opacity: 0.7,
                                transition: 'opacity 0.2s ease',
                                '&:hover': {
                                  opacity: 1,
                                  backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(schedule);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            {schedule.is_recurring && (
                              <AllInclusiveIcon
                                sx={{
                                  position: 'absolute',
                                  right: 30,
                                  top: 2,
                                  fontSize: '14px',
                                  color: '#333333',
                                  opacity: 0.7
                                }}
                              />
                            )}
                          </>
                        )}

                        {(isStartDay || !isMultiDay) && (
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            pr: (isStartDay || !isMultiDay) ? 5 : 0
                          }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#333333',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              {schedule.auxiliaire.name}
                              {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                                <StickyNote2OutlinedIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '11px',
                                color: '#333333',
                                opacity: 0.9
                              }}
                            >
                              {schedule.auxiliaire.category}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '11px',
                                color: '#333333',
                                opacity: 0.9
                              }}
                            >
                              {getScheduleTimeDisplay(schedule, day.date)}
                            </Typography>
                          </Box>
                        )}

                        {/* Continuation of an overnight shift. It names the carer
                            like any other block: an unlabelled colour block tells
                            the reader nothing, and with a 20:00-08:00 rota there
                            is one on almost every day. */}
                        {isMultiDay && !isStartDay && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pr: 4 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#333333',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Box component="span" sx={{ opacity: 0.7 }}>↳</Box>
                              {schedule.auxiliaire.name}
                              {notesByScheduleId[schedule.id] && notesByScheduleId[schedule.id].length > 0 && (
                                <StickyNote2OutlinedIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '11px',
                                color: '#333333',
                                opacity: 0.85
                              }}
                            >
                              {getScheduleTimeDisplay(schedule, day.date)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Supprimer un planning récurrent</DialogTitle>
        <DialogContent>
          <Typography>
            Ce planning est récurrent. Souhaitez-vous supprimer uniquement cette occurrence ou toutes les occurrences futures ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Annuler
          </Button>
          <Button 
            onClick={() => {
              if (selectedSchedule) {
                handleDeleteSchedule(selectedSchedule.id, false);
              }
              setDeleteDialogOpen(false);
            }}
            sx={{ borderRadius: 2 }}
          >
            Supprimer cette occurrence
          </Button>
          <Button 
            onClick={() => {
              if (selectedSchedule) {
                handleDeleteSchedule(selectedSchedule.id, true);
              }
              setDeleteDialogOpen(false);
            }}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Supprimer toutes les occurrences futures
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add ScheduleEditDialog */}
      <ScheduleEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        schedule={selectedSchedule}
        auxiliaires={auxiliaires}
        onSave={handleEditSchedule}
      />

      {/* Note Dialog */}
      <NoteDialog
        open={noteDialogOpen}
        onClose={() => {
          setNoteDialogOpen(false);
          setNoteSchedule(null);
        }}
        onSave={handleSaveNote}
      />

      <NotesDialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        scheduleId={noteSchedule?.id ?? null}
        onUpdated={(updated: NoteItem[]) => {
          if (noteSchedule?.id) {
            setNotesByScheduleId((prev: Record<number, NoteItem[]>) => ({ ...prev, [noteSchedule.id!]: updated }));
          }
        }}
      />

      {/* Creation dialog for adding a schedule on a specific day */}
      <ScheduleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        auxiliaires={auxiliaires}
        onSave={handleSaveSchedule}
        initialDate={creationDate ?? undefined}
      />
    </Box>
  );
};

export default WeekView; 